import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { fetchProductReviews } from "../lib/api";
import { postCustomerReview } from "../lib/customerApi";
import { useCustomerAuth } from "../context/CustomerAuthContext";

export default function ProductReviews({ productSlug }) {
  const { user } = useCustomerAuth();
  const [data, setData] = useState({ average: 0, count: 0, reviews: [] });
  const [writing, setWriting] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const load = () => fetchProductReviews(productSlug).then(setData).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [productSlug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await postCustomerReview({ product_slug: productSlug, rating, title, body });
      toast.success("Review posted");
      setTitle("");
      setBody("");
      setWriting(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post review");
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-[#a89e80]" data-testid="pdp-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="eyebrow">Reviews & ratings</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3">
            {data.count > 0 ? `${data.average}/5` : "Be the first to review"}
          </h2>
          {data.count > 0 && (
            <p className="text-[#7A7A7A] text-sm mt-2">Based on {data.count} verified reviews</p>
          )}
        </div>
        {user && user !== false && (
          <button
            onClick={() => setWriting((w) => !w)}
            className="text-[11px] tracking-[0.22em] uppercase border border-[#1A1A1A] px-5 py-3 hover:bg-[#1A1A1A] hover:text-[#c3ba9e]"
            data-testid="pdp-write-review"
          >
            {writing ? "Cancel" : "Write a review"}
          </button>
        )}
      </div>

      {writing && (
        <form onSubmit={submit} className="bg-[#b8af92] p-6 mb-10 space-y-4 max-w-2xl" data-testid="pdp-review-form">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} stars`}>
                <Star size={22} className={n <= rating ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#7A7A7A]"} />
              </button>
            ))}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="w-full border border-[#a89e80] bg-transparent px-3 py-2.5" />
          <textarea required minLength={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share how the piece fits and feels…" rows={4} className="w-full border border-[#a89e80] bg-transparent px-3 py-2.5 resize-none" data-testid="pdp-review-body" />
          <button type="submit" className="bg-[#1A1A1A] text-[#c3ba9e] px-6 py-3 text-[11px] tracking-[0.22em] uppercase" data-testid="pdp-review-submit">Post review</button>
        </form>
      )}

      {data.reviews.length === 0 ? (
        <p className="text-[#7A7A7A] text-sm">No reviews yet.</p>
      ) : (
        <div className="space-y-8 max-w-3xl">
          {data.reviews.map((r) => (
            <article key={r.id} className="border-b border-[#a89e80] pb-7" data-testid={`pdp-review-${r.id}`}>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < r.rating ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#7A7A7A]"} />
                ))}
                {r.verified_purchase && (
                  <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-[#3b6e4a]">Verified purchase</span>
                )}
              </div>
              {r.title && <h3 className="font-serif-display text-2xl">{r.title}</h3>}
              <p className="text-[#3a3a3a] mt-2 leading-relaxed whitespace-pre-wrap">{r.body}</p>
              <p className="text-[11px] text-[#7A7A7A] mt-3 tracking-wider">
                {r.author_name} · {new Date(r.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
